import React, { useState } from 'react';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { sentryService } from '../../services/sentry';

const ErrorHandlingExample: React.FC = () => {
  const [count, setCount] = useState(0);
  const [inputValue, setInputValue] = useState('');

  // Use the error handling hook
  const {
    handleError,
    handleAsyncError,
    clearError,
    error,
    hasError,
    captureMessage
  } = useErrorHandler();

  // Check Sentry configuration status
  const sentryStatus = sentryService.getConfigurationStatus();

  // Example of a function that might throw an error
  const riskyOperation = (value: number) => {
    if (value === 0) {
      throw new Error('Cannot divide by zero!');
    }
    if (value < 0) {
      throw new Error('Negative numbers are not allowed');
    }
    return 100 / value;
  };

  // Example of an async operation that might fail
  const asyncOperation = async (delay: number): Promise<string> => {
    await new Promise(resolve => setTimeout(resolve, delay));

    if (delay > 2000) {
      throw new Error('Operation took too long!');
    }

    return `Operation completed in ${delay}ms`;
  };

  // Handle synchronous errors
  const handleSyncError = () => {
    try {
      const result = riskyOperation(count);
      captureMessage(`Operation successful: ${result}`, 'info');
    } catch (error) {
      handleError(error instanceof Error ? error : new Error(String(error)), {
        operation: 'syncOperation',
        count,
      });
    }
  };

  // Handle asynchronous errors
  const handleAsyncErrorExample = async () => {
    try {
      const result = await asyncOperation(parseInt(inputValue) || 1000);
      captureMessage(`Async operation successful: ${result}`, 'info');
    } catch (error) {
      handleAsyncError(error instanceof Error ? error : new Error(String(error)), {
        operation: 'asyncOperation',
        delay: inputValue,
      });
    }
  };

  // Simulate a React error
  const triggerReactError = () => {
    throw new Error('This is a simulated React error for testing error boundaries');
  };

  if (hasError) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-lg font-medium text-red-800 mb-2">
          Error State
        </h3>
        <p className="text-red-700 mb-4">
          {error?.message}
        </p>
        <div className="space-x-2">
          <button
            onClick={clearError}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Clear Error
          </button>
          <button
            onClick={() => setCount(0)}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Reset Counter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Error Handling Examples
      </h2>

      {/* Sentry Configuration Status */}
      <div className={`p-3 rounded-lg mb-6 ${
        sentryStatus.isProperlyConfigured
          ? 'bg-green-50 border border-green-200'
          : 'bg-yellow-50 border border-yellow-200'
      }`}>
        <div className="flex items-center">
          <span className={`text-lg mr-2 ${
            sentryStatus.isProperlyConfigured ? 'text-green-600' : 'text-yellow-600'
          }`}>
            {sentryStatus.isProperlyConfigured ? '✅' : '⚠️'}
          </span>
          <div>
            <h4 className={`font-medium ${
              sentryStatus.isProperlyConfigured ? 'text-green-800' : 'text-yellow-800'
            }`}>
              Sentry Configuration Status
            </h4>
            <p className={`text-sm ${
              sentryStatus.isProperlyConfigured ? 'text-green-700' : 'text-yellow-700'
            }`}>
              {sentryStatus.isProperlyConfigured
                ? 'Error tracking is properly configured and active'
                : 'Error tracking is not properly configured. Errors will be logged locally but not sent to Sentry.'
              }
            </p>
            {!sentryStatus.isProperlyConfigured && (
              <p className="text-xs text-yellow-600 mt-1">
                Check console for configuration details and configure getSentryDsn() in Environment service.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Counter Section */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Counter: {count}</h3>
          <div className="space-x-2">
            <button
              onClick={() => setCount(c => c + 1)}
              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Increment
            </button>
            <button
              onClick={() => setCount(c => c - 1)}
              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Decrement
            </button>
            <button
              onClick={() => setCount(0)}
              className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Input Section */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Async Operation Delay</h3>
          <input
            type="number"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Enter delay in milliseconds"
            className="px-3 py-2 border border-gray-300 rounded mr-2"
          />
          <button
            onClick={handleAsyncErrorExample}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            Run Async Operation
          </button>
        </div>

        {/* Error Testing Section */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Test Error Handling</h3>
          <div className="space-x-2">
            <button
              onClick={handleSyncError}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Test Sync Error
            </button>
            <button
              onClick={triggerReactError}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
            >
              Trigger React Error
            </button>
          </div>
        </div>

        {/* Instructions */}
        <div className="p-4 bg-blue-50 rounded-lg">
          <h3 className="text-lg font-medium text-blue-900 mb-2">How to Test</h3>
          <ul className="text-blue-800 text-sm space-y-1">
            <li>• Set counter to 0 and click "Test Sync Error" to see division by zero error</li>
            <li>• Set counter to negative and click "Test Sync Error" to see custom error</li>
            <li>• Enter a delay &gt; 2000ms and click "Run Async Operation" to see timeout error</li>
            <li>• Click "Trigger React Error" to test error boundary (will show error modal)</li>
            <li>• All errors are automatically captured and sent to Sentry</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ErrorHandlingExample;
